/**
 * Custom hooks for property files and images.
 * These are workflow hooks (not generated) since they use multipart upload
 * and nested resource endpoints (/api/properties/:id/files, /api/properties/:id/images).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../api/http-client.js';
import { propertyImagesKeys } from '../api/generated_hooks/generated-property_images-hooks';
import { propertyFilesKeys } from '../api/generated_hooks/generated-property_files-hooks';

// ═══════════════════════════════════════════
// Types
// ═══════════════════════════════════════════

export interface PropertyFile {
  id: string;
  property_id: string;
  data_source_id: string | null;
  file_name: string;
  mime_type: string;
  storage_key: string;
  file_url: string | null;
  description: string | null;
  uploaded_by_user_profile_id: string | null;
  /** Who uploaded it (the list embeds it); null on files from before uploads recorded it (2026-10-29). */
  uploaded_by?: { id: string; full_name: string | null } | null;
  created_at: string;
  /**
   * Signed download URL, batch-minted server-side in the list response
   * (B1b). Lets rows render previews / links without a per-file
   * `/files/:fileId/url` request. Absent only on responses from an older
   * API build — callers fall back to `usePropertyFileUrl` in that case.
   */
  signed_url?: string | null;
}

export interface PropertyImage {
  id: string;
  property_id: string;
  data_source_id: string | null;
  file_name: string;
  storage_key: string;
  file_url: string | null;
  caption: string | null;
  display_order: number;
  is_primary: boolean;
  created_by_user_profile_id: string | null;
  created_at: string;
  signed_url?: string | null;
}

// ═══════════════════════════════════════════
// Query Keys
// ═══════════════════════════════════════════

export const propertyFileKeys = {
  all: (propertyId: string) => ['property-files', propertyId] as const,
  files: (propertyId: string) => [...propertyFileKeys.all(propertyId), 'files'] as const,
  images: (propertyId: string) => [...propertyFileKeys.all(propertyId), 'images'] as const,
  /** The archived photos and files (D2-S F1). */
  archived: (propertyId: string) => [...propertyFileKeys.all(propertyId), 'archived'] as const,
  /** Card view's batched hero read (RP-4). No per-property key reaches it — main.tsx invalidates the root on every mutation. */
  heroesRoot: ['property-files', 'heroes'] as const,
  heroes: (propertyIds: readonly string[]) => [...propertyFileKeys.heroesRoot, propertyIds.join(',')] as const,
};

// ═══════════════════════════════════════════
// File Hooks
// ═══════════════════════════════════════════

export function usePropertyFiles(propertyId: string) {
  return useQuery({
    queryKey: propertyFileKeys.files(propertyId),
    queryFn: async () => {
      const res = await httpClient.get<{ data: PropertyFile[]; success: boolean }>(
        `/properties/${propertyId}/files`
      );
      return res.data;
    },
    enabled: !!propertyId,
  });
}

export function useUploadPropertyFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ propertyId, file }: { propertyId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/properties/${propertyId}/files`,
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(err.error || 'Upload failed');
      }

      return res.json();
    },
    onSuccess: (_, { propertyId }) => {
      queryClient.invalidateQueries({ queryKey: propertyFileKeys.files(propertyId) });
    },
  });
}

export function useDeletePropertyFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ propertyId, fileId }: { propertyId: string; fileId: string }) => {
      await httpClient.delete(`/properties/${propertyId}/files/${fileId}`);
    },
    onSuccess: (_, { propertyId }) => {
      queryClient.invalidateQueries({ queryKey: propertyFileKeys.files(propertyId) });
      // A hard delete is also offered from the Archived list.
      queryClient.invalidateQueries({ queryKey: propertyFileKeys.archived(propertyId) });
    },
  });
}

export function usePropertyFileUrl(
  propertyId: string,
  fileId: string,
  options?: { enabled?: boolean }
) {
  // B1b: callers that already have a batch-minted `signed_url` from the
  // list response pass { enabled: false } to skip this per-file request.
  const enabled = (options?.enabled ?? true) && !!propertyId && !!fileId;
  return useQuery({
    queryKey: ['property-file-url', propertyId, fileId],
    queryFn: async () => {
      const res = await httpClient.get<{ data: { url: string; fileName: string }; success: boolean }>(
        `/properties/${propertyId}/files/${fileId}/url`
      );
      return res.data;
    },
    enabled,
  });
}

// ═══════════════════════════════════════════
// Image Hooks
// ═══════════════════════════════════════════

export function usePropertyImages(propertyId: string) {
  return useQuery({
    queryKey: propertyFileKeys.images(propertyId),
    queryFn: async () => {
      const res = await httpClient.get<{ data: PropertyImage[]; success: boolean }>(
        `/properties/${propertyId}/images`
      );
      return res.data;
    },
    enabled: !!propertyId,
  });
}

/**
 * The hero image of each property in `propertyIds` — one request for a whole
 * card-view page (RP-4; it was one `/properties/:id/images` per card). Ids are
 * de-duplicated and sorted, so the same page in any sort order shares one
 * cache entry. The server picks the hero the way `pickHeroImage` does
 * (PropertyHeroImage.tsx); a property without images has no row.
 */
export function usePropertyHeroImages(propertyIds: readonly string[]) {
  const ids = [...new Set(propertyIds.filter(Boolean))].sort();
  return useQuery({
    queryKey: propertyFileKeys.heroes(ids),
    queryFn: async () => {
      const res = await httpClient.get<{ data: PropertyImage[]; success: boolean }>(
        `/properties/hero-images?ids=${ids.join(',')}`
      );
      return res.data;
    },
    enabled: ids.length > 0,
  });
}

export function useUploadPropertyImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ propertyId, file }: { propertyId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/properties/${propertyId}/images`,
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(err.error || 'Upload failed');
      }

      return res.json();
    },
    onSuccess: (_, { propertyId }) => {
      queryClient.invalidateQueries({ queryKey: propertyFileKeys.images(propertyId) });
    },
  });
}

export function useUpdatePropertyImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      imageId,
      data,
    }: {
      propertyId: string;
      imageId: string;
      data: { caption?: string; display_order?: number; is_primary?: boolean };
    }) => {
      const res = await httpClient.put<{ data: PropertyImage; success: boolean }>(
        `/properties/${propertyId}/images/${imageId}`,
        data
      );
      return res.data;
    },
    onSuccess: (_, { propertyId }) => {
      queryClient.invalidateQueries({ queryKey: propertyFileKeys.images(propertyId) });
    },
  });
}

export function useDeletePropertyImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ propertyId, imageId }: { propertyId: string; imageId: string }) => {
      await httpClient.delete(`/properties/${propertyId}/images/${imageId}`);
    },
    onSuccess: (_, { propertyId }) => {
      queryClient.invalidateQueries({ queryKey: propertyFileKeys.images(propertyId) });
      // Also invalidate the generated key tree so the PhotoActionCard
      // counter on PropertyDetailPage refreshes. The list query feeds
      // the carousel; the count query feeds the action card badge —
      // they live on different key trees, so both must be busted.
      queryClient.invalidateQueries({ queryKey: propertyImagesKeys.all });
      queryClient.invalidateQueries({ queryKey: propertyFileKeys.archived(propertyId) });
    },
  });
}

// ═══════════════════════════════════════════
// Archive (D2-S F1)
// ═══════════════════════════════════════════

/** A photo or a file: the URL segment the archive routes use. */
export type AttachmentKind = 'images' | 'files';

/** Who archived it, when and why — on every row of the Archived list. */
export interface ArchiveStamp {
  archived_at: string;
  archive_reason: string | null;
  archived_by?: { id: string; full_name: string | null } | null;
}

export interface ArchivedPropertyAttachments {
  images: Array<PropertyImage & ArchiveStamp>;
  files: Array<PropertyFile & ArchiveStamp>;
}

/** The property's archived photos and files, newest first (each with a signed URL). */
export function useArchivedPropertyAttachments(propertyId: string) {
  return useQuery({
    queryKey: propertyFileKeys.archived(propertyId),
    queryFn: async () => {
      const res = await httpClient.get<{ data: ArchivedPropertyAttachments; success: boolean }>(
        `/properties/${propertyId}/archived-attachments`
      );
      return res.data;
    },
    enabled: !!propertyId,
  });
}

/** Every list an archive or a restore changes: the gallery / files list, the action cards' counts (generated trees), the Archived list. */
function invalidateAttachments(queryClient: ReturnType<typeof useQueryClient>, propertyId: string, kind: AttachmentKind) {
  queryClient.invalidateQueries({ queryKey: kind === 'images' ? propertyFileKeys.images(propertyId) : propertyFileKeys.files(propertyId) });
  queryClient.invalidateQueries({ queryKey: propertyFileKeys.archived(propertyId) });
  queryClient.invalidateQueries({ queryKey: kind === 'images' ? propertyImagesKeys.all : propertyFilesKeys.all });
}

/** Archive a photo or a file: it leaves the lists, its stored object is kept, it can be restored. */
export function useArchivePropertyAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ propertyId, kind, id, reason }: { propertyId: string; kind: AttachmentKind; id: string; reason?: string | null }) => {
      const body = reason ? { reason } : {};
      // Literal URLs, so the frontend → API URL contract test checks each one.
      if (kind === 'images') await httpClient.post(`/properties/${propertyId}/images/${id}/archive`, body);
      else await httpClient.post(`/properties/${propertyId}/files/${id}/archive`, body);
    },
    onSuccess: (_, { propertyId, kind }) => invalidateAttachments(queryClient, propertyId, kind),
  });
}

export function useRestorePropertyAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ propertyId, kind, id }: { propertyId: string; kind: AttachmentKind; id: string }) => {
      if (kind === 'images') await httpClient.post(`/properties/${propertyId}/images/${id}/restore`, {});
      else await httpClient.post(`/properties/${propertyId}/files/${id}/restore`, {});
    },
    onSuccess: (_, { propertyId, kind }) => invalidateAttachments(queryClient, propertyId, kind),
  });
}
